import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';

/**
 * Asked once, right after a creator is verified: what do you post about? niche_ids drives
 * almost everything a creator gets — matched brand jobs, push notifications, peer ranking,
 * brands to pitch — and the claim flow never asked for it: 11 of 20 verified creators had none,
 * so their Home showed «скоро» tiles and they received no niche-matched job pushes.
 * Skippable (the Home card keeps reminding); never blocks the app.
 */
export default function NichePrompt({ providerId, onDone }: { providerId: string; onDone: () => void }) {
  const { t, i18n } = useTranslation('provider');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o }) as string;
  const invalidate = useInvalidateSupabase();
  const [sel, setSel] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data } = useSupabaseQuery<{ id: string; name_ru: string; name_en: string | null }[]>(['niches-all-i18n'],
    async () => supabase.from('niches').select('id, name_ru, name_en').eq('is_active', true).order('name_ru'));
  const label = (n: { name_ru: string; name_en: string | null }) => (i18n.language === 'en' && n.name_en) || n.name_ru;
  const shown = (data ?? []).filter((n) => !filter || label(n).toLowerCase().includes(filter.toLowerCase()));
  const toggle = (id: string) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= 3 ? s : [...s, id]));

  const save = async () => {
    setSaving(true); setErr(null);
    const { error } = await supabase.from('instagram_providers').update({ niche_ids: sel }).eq('id', providerId);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    invalidate(['creator-digest']); invalidate(['creator-collabs']); invalidate(['creator-pitch']); invalidate(['creator-peers']);
    onDone();
  };
  const skip = () => { try { sessionStorage.setItem('adamar_niche_skip', '1'); } catch { /* ignore */ } onDone(); };

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '24px 16px' }}>
      <h2 style={{ color: 'var(--text)', margin: '0 0 6px', fontSize: 20 }}>{tr('creator.niche.title', 'О чём ваш блог?')}</h2>
      <p style={{ color: 'var(--muted)', fontSize: 13, margin: '0 0 14px', lineHeight: 1.5 }}>
        {tr('creator.niche.why', 'Выберите до 3 тем — по ним мы подбираем задания брендов, присылаем уведомления и сравниваем вас с похожими блогерами.')}
      </p>
      <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={tr('creator.profile.search', 'Поиск ниши…')}
        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, background: 'rgba(0,0,0,0.25)', color: 'var(--text)', border: '1px solid rgba(74,158,255,0.25)', fontSize: 16, marginBottom: 10, fontFamily: 'inherit' }} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
        {shown.map((n) => {
          const on = sel.includes(n.id);
          return (
            <span key={n.id} onClick={() => toggle(n.id)} style={{ cursor: 'pointer', padding: '6px 11px', borderRadius: 16, fontSize: 13, userSelect: 'none',
              border: `1px solid ${on ? 'rgba(74,158,255,0.7)' : 'rgba(255,255,255,0.14)'}`, background: on ? 'rgba(74,158,255,0.18)' : 'transparent', color: on ? '#dbe7ff' : 'var(--muted)' }}>
              {label(n)}
            </span>
          );
        })}
      </div>
      {err && <div style={{ color: '#e8607a', fontSize: 13, marginTop: 8 }}>{err}</div>}
      <button type="button" disabled={sel.length === 0 || saving} onClick={save}
        style={{ marginTop: 16, width: '100%', padding: '12px 16px', borderRadius: 8, border: 'none', color: '#fff', fontWeight: 600, fontSize: 15, fontFamily: 'inherit',
          background: 'var(--primary)', opacity: sel.length === 0 || saving ? 0.5 : 1, cursor: sel.length === 0 ? 'default' : 'pointer' }}>
        {saving ? '…' : tr('creator.niche.save', 'Сохранить ({{n}}/3)', { n: sel.length })}
      </button>
      <button type="button" onClick={skip}
        style={{ marginTop: 10, width: '100%', background: 'none', border: 0, color: 'var(--dim)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, textDecoration: 'underline' }}>
        {tr('creator.niche.skip', 'Позже')}
      </button>
    </div>
  );
}
